import { useEffect, useMemo, useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  MessageCircle, Clock, Search, Target, Heart,
  ChevronRight, BookOpen, MapPin, GraduationCap, MailOpen, Mail, Coins,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { StudentSidebar } from "@/components/student/dashboard/StudentSidebar";
import { GroupClassesPanel } from "@/components/student/dashboard/GroupClassesPanel";
import { CrashCoursesPanel } from "@/components/student/dashboard/CrashCoursesPanel";
import { ProfileNudges } from "@/components/student/dashboard/ProfileNudges";
import { NextStepsCard } from "@/components/common/NextStepsCard";
import { useStudentNextSteps } from "@/hooks/useNextSteps";
import { useDashboardTour } from "@/hooks/useDashboardTour";
import { studentDashboardTour } from "@/lib/tours/dashboardTours";
import { ComingUpPanel } from "@/components/student/dashboard/ComingUpPanel";
import { RecommendedOfferings } from "@/components/student/dashboard/RecommendedOfferings";
import { getTeacherByHandle } from "@/services/marketplaceService";
import { OPEN_THREAD_STATUSES, type InquiryThread } from "@/services/inquiryService";
import { useMyInquiries } from "@/hooks/use-inquiries";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useGoalProfile } from "@/hooks/useGoalProfile";
import { useWishlist } from "@/hooks/useWishlist";
import SearchResultsPage from "@/pages/public/SearchResultsPage";
import { useExamTargets } from "@/hooks/useExamTargets";
import { daysUntil } from "@/services/examTargetService";
import { gradeLabel } from "@/lib/goalOptions";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { EmptyState } from "@/components/common/EmptyState";

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const date = d.toLocaleDateString([], sameYear ? { month: "short", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" });
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

function displayTeacherName(name: string): string {
  if (!name.includes("@")) return name;
  return name.split("@")[0].split(/[._-]+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function ActiveInquiryRow({ thread }: { thread: InquiryThread }) {
  const name = displayTeacherName(thread.teacherName);
  const handle = thread.teacherHandle;
  const isUnread = thread.unreadCount > 0;
  const { data: prof } = useQuery({
    queryKey: ["teacherBySlug", handle],
    queryFn: () => getTeacherByHandle(handle!),
    enabled: !!handle,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const location = prof?.locationDisplayName || prof?.city || null;
  const ts = thread.lastMessage?.createdAt ?? thread.updatedAt;
  return (
    <Link
      to={`/inquiry/${thread.id}`}
      className={cn(
        "group relative flex items-center gap-3 rounded-xl px-4 py-3 overflow-hidden",
        "ring-1 transition-all duration-150 hover:shadow-md hover:-translate-y-0.5",
        isUnread
          ? "bg-indigo-50/50 ring-indigo-100 hover:ring-indigo-200"
          : "bg-white ring-slate-200/70 hover:ring-slate-300"
      )}
    >
      {/* Unread accent bar */}
      {isUnread && <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-500 to-violet-500" />}
      <div className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
        isUnread ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-500"
      )}>
        {getInitials(name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={cn("text-sm truncate", isUnread ? "font-bold text-slate-900" : "font-semibold text-slate-800")}>
            {name}
          </span>
          <span className="inline-flex items-center rounded-full bg-indigo-100/80 text-indigo-700 text-xs font-semibold px-2 py-0.5 max-w-[180px] truncate">
            {thread.subject}
          </span>
        </div>
        {location && (
          <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" /> {location}
          </div>
        )}
        {isUnread ? (
          <div className="text-xs text-rose-600 flex items-center gap-1 mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            New reply · {thread.lastMessage?.content?.slice(0, 50) ?? "open conversation"}
          </div>
        ) : (
          <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" />
            Awaiting reply{thread.lastMessage ? ` · ${thread.lastMessage.content.slice(0, 50)}` : ""}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span className="text-xs text-slate-400 hidden sm:block">{formatDateTime(ts)}</span>
        {isUnread
          ? <Mail className="w-4 h-4 text-indigo-500" />
          : <MailOpen className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
        }
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}

function SectionCard({
  icon: Icon,
  title,
  count,
  action,
  children,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white/90 backdrop-blur-[2px] shadow-sm p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-indigo-600" />
        </div>
        <span className="text-sm font-bold text-slate-800">{title}</span>
        {count != null && count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold px-1.5">
            {count}
          </span>
        )}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </div>
  );
}

export default function StudentDashboard() {
  usePageTitle('Student Dashboard | BookMyCoaching');
  const { profile } = useStudentProfile();
  const { user, isLoading: userLoading } = useCurrentUser();
  const navigate = useNavigate();

  // coinBalance is null if this student never received the registration
  // grant (e.g. added STUDENT via role-switching, not as their first-ever
  // role) — must render as no-stat, not a zero.
  const coinBalance = profile?.coinBalance ?? null;

  useEffect(() => {
    const welcome = sessionStorage.getItem("role_switch_welcome");
    if (welcome) {
      sessionStorage.removeItem("role_switch_welcome");
      toast.success(`Welcome! You're now also a ${welcome === "TEACHER" ? "Teacher" : "Student"}.`);
    }
  }, []);

  useEffect(() => {
    const coinWelcome = sessionStorage.getItem("student_coin_bonus_welcome");
    if (coinWelcome && coinBalance != null) {
      sessionStorage.removeItem("student_coin_bonus_welcome");
      toast.success(`Welcome! You've received ${coinBalance} BMC coins.`);
    }
  }, [coinBalance]);

  // Redirect to goal onboarding if the student hasn't completed it yet
  useEffect(() => {
    if (userLoading) return;
    if (user && user.goalOnboardingCompleted === false) {
      navigate("/goal-onboarding", { replace: true });
    }
  }, [user, userLoading, navigate]);

  // All hooks must be called before any early return (rules of hooks)
  const { data: inquiriesData } = useMyInquiries(0, 10);
  const { data: goal } = useGoalProfile();
  const nextSteps = useStudentNextSteps();
  // One-time spotlight tour once goal onboarding is complete and data settled.
  useDashboardTour(
    "student",
    studentDashboardTour,
    nextSteps.isReady && !userLoading && user?.goalOnboardingCompleted === true,
  );
  const { items: saved } = useWishlist();
  const { targets: examTargets } = useExamTargets();

  const threads = inquiriesData?.content ?? [];
  const activeThreads = threads.filter((t) => OPEN_THREAD_STATUSES.has(t.status));
  const unreadCount = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  const lastActivity = (t: typeof threads[number]) =>
    new Date(t.lastMessage?.createdAt ?? t.updatedAt).getTime();
  const sortedActive = [...activeThreads].sort((a, b) => lastActivity(b) - lastActivity(a));
  const topActive = sortedActive.slice(0, 3);
  const extraActive = activeThreads.length - topActive.length;

  const displayName = profile?.aboutMe?.fullName || user?.name || user?.email || "Student";
  const firstName = displayName.split(" ")[0];

  const state: "DAY1" | "ACTIVE" | "RETURNING" =
    activeThreads.length > 0 ? "ACTIVE" : threads.length === 0 ? "DAY1" : "RETURNING";

  const [view, setView] = useState<"dashboard" | "search">("dashboard");
  const [, setSearchParams] = useSearchParams();

  // Opens the embedded Find Teachers view (same behavior as the sidebar's
  // "Find Teachers" item), optionally pre-filled with a search query.
  const openSearch = (q?: string | null) => {
    if (q) setSearchParams({ q, page: "0" });
    setView("search");
  };

  // Block render until we know whether to redirect — prevents dashboard flash
  if (userLoading || (user && user.goalOnboardingCompleted === false)) {
    return <div className="min-h-screen bg-white" />;
  }

  const nearestExam = examTargets.length > 0 ? examTargets[0] : null;
  const goalSummary = goal
    ? [goal.subject, gradeLabel(goal.gradeBand) || goal.gradeBand, goal.goalType].filter(Boolean).join(" · ")
    : "";

  const stats = [
    { label: "Active", value: activeThreads.length, icon: MessageCircle, to: "/my-inquiries" },
    { label: "Unread", value: unreadCount, icon: Mail, to: "/my-inquiries" },
    { label: "Saved", value: saved.length, icon: Heart, to: "/saved" },
    {
      label: nearestExam ? nearestExam.examName : "Exam",
      value: nearestExam ? `${Math.max(0, daysUntil(nearestExam.examDate))}d` : "—",
      icon: Target,
      to: "/my-goal",
    },
    // Only shown for students who actually received the registration bonus —
    // a dual-role student who added STUDENT via role-switching has no
    // balance (coinBalance is null) and must see no stat here at all.
    ...(coinBalance != null
      ? [{ label: "Coins Available", value: coinBalance, icon: Coins, to: "/student-coins" }]
      : []),
  ];

  // ── section renderers ──
  const inquiriesSection = (
    <SectionCard
      icon={MessageCircle}
      title="Active Inquiries"
      count={activeThreads.length}
      action={
        activeThreads.length > 0 ? (
          <Button asChild variant="ghost" size="sm" className="text-indigo-600 text-xs gap-1">
            <Link to="/my-inquiries">View All <ChevronRight className="w-3.5 h-3.5" /></Link>
          </Button>
        ) : undefined
      }
    >
      {activeThreads.length === 0 ? (
        <div className="rounded-xl bg-slate-50/80">
          <EmptyState
            icon={MessageCircle}
            title={state === "RETURNING" ? "No open conversations" : "No inquiries yet"}
            description={state === "RETURNING" ? "No open conversations right now." : "You haven't reached out to a teacher yet."}
            iconBgClassName="bg-indigo-100"
            iconClassName="text-indigo-500"
            compact
            action={
              <Button size="sm" onClick={() => setView("search")}>
                <Search className="w-4 h-4 mr-1.5" /> Find a teacher
              </Button>
            }
          />
        </div>
      ) : (
        <div className="space-y-2">
          {topActive.map((t) => <ActiveInquiryRow key={t.id} thread={t} />)}
          {extraActive > 0 && (
            <Link to="/my-inquiries" className="block text-center text-sm text-indigo-600 hover:text-indigo-700 pt-1">
              {extraActive} more {extraActive === 1 ? "conversation" : "conversations"} →
            </Link>
          )}
        </div>
      )}
    </SectionCard>
  );

  // Offering-centric recommendations (Layout D): one blended goal-ranked rail
  // with format chips, replacing the old teacher-only carousel. Self-hides when
  // there is nothing to recommend.
  const recommendationsSection = <RecommendedOfferings />;

  const savedPanel = (
    <div className="rounded-xl bg-slate-100/80 overflow-hidden">
      <div className="px-4 pt-3 pb-2.5 flex items-center gap-2.5 bg-pink-50/60">
        <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-pink-100 text-pink-600 shrink-0"><Heart className="w-3.5 h-3.5" /></span>
        <span className="text-sm font-bold text-slate-800 flex-1">Saved Teachers</span>
        {saved.length > 0 && (
          <Link to="/saved" className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold">View all</Link>
        )}
      </div>
      {saved.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No saved teachers"
          description="Browse teachers and save your favourites here."
          iconBgClassName="bg-pink-100"
          iconClassName="text-pink-400"
          compact
          action={
            <button onClick={() => setView("search")} className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold">
              Browse teachers →
            </button>
          }
        />
      ) : (
        <div className="p-2 space-y-1.5">
          {saved.slice(0, 6).map((s) => (
            <Link
              key={s.id}
              to={`/t/${s.teacherHandle}`}
              className="flex items-center gap-2.5 rounded-lg bg-white ring-1 ring-slate-200/70 px-3 py-2.5 hover:ring-pink-300 hover:shadow-sm transition-all duration-150"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-xs font-semibold text-indigo-600 shrink-0">
                {getInitials(s.teacherName)}
              </div>
              <span className="text-sm flex-1 truncate text-slate-700 font-medium">{s.teacherName}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  const ordered = state === "ACTIVE"
    ? [inquiriesSection, recommendationsSection]
    : [recommendationsSection, inquiriesSection];

  return (
    <div className="min-h-screen bg-background student-theme">
      <SidebarProvider>
        <DashboardTopNav role="student" />
        <div className="min-h-screen flex w-full pt-14">
          <StudentSidebar studentName={displayName} studentAvatar="" unreadInquiries={unreadCount} onFindTeachers={() => setView("search")} findTeachersActive={view === "search"} onNavClick={() => setView("dashboard")} />

          <div className="flex-1 flex flex-col min-w-0 relative">
            <div className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
              backgroundImage: "radial-gradient(hsl(262,45%,14%) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }} />
            {/* Main Content */}
            <main className="relative flex-1 overflow-auto">

            {view === "search" ? (
              <SearchResultsPage embedded onBack={() => setView("dashboard")} />
            ) : (<div className="p-4 md:p-6">

              {/* Header card: welcome + goal + stats */}
              <div className="relative rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 shadow-lg shadow-indigo-500/20 mb-6 overflow-hidden">
                {/* Decorative glows */}
                <div className="absolute -top-16 -left-10 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-20 right-10 w-64 h-64 bg-fuchsia-400/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
                  backgroundImage: "radial-gradient(white 1px, transparent 1px)",
                  backgroundSize: "22px 22px",
                }} />
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-5">
                  {/* Left: welcome + goal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-indigo-200" />
                      <span className="text-xs font-bold uppercase tracking-widest text-indigo-200">Student Dashboard</span>
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white">
                      {firstName ? `Welcome back, ${firstName}` : "Dashboard"}
                    </h1>
                    {goalSummary ? (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 ring-1 ring-white/20 text-white/90 text-xs font-medium px-2.5 py-1">
                          <GraduationCap className="w-3.5 h-3.5" /> {goalSummary}
                        </span>
                        <Link to="/my-goal" className="text-xs text-indigo-100 hover:text-white font-semibold underline-offset-2 hover:underline">
                          Edit goal
                        </Link>
                      </div>
                    ) : (
                      <p className="text-sm text-indigo-100 mt-1">Here's an overview of your learning journey</p>
                    )}
                  </div>

                  {/* Right: stat pills */}
                  <div className="flex flex-wrap items-stretch gap-2 sm:gap-2.5 shrink-0">
                    {stats.map((stat) => {
                      const Icon = stat.icon;
                      return (
                        <Link
                          key={stat.label}
                          to={stat.to}
                          className="group flex flex-col items-center justify-center min-w-[76px] rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur-sm px-3 py-2.5 transition-all duration-150 hover:bg-white/20 hover:ring-white/40 hover:-translate-y-0.5"
                        >
                          <div className="flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5 text-indigo-100 group-hover:text-white transition-colors" />
                            <span className="text-lg sm:text-xl font-bold text-white leading-none">{stat.value}</span>
                          </div>
                          <span className="text-xs font-medium text-indigo-100 mt-1 leading-tight text-center max-w-[110px] truncate">{stat.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pace nudge */}
              {nearestExam && (
                <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50/60 px-4 py-3 flex items-center gap-3 mb-6 shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Target className="w-[18px] h-[18px] text-amber-600" />
                  </div>
                  <div className="text-sm text-amber-900 flex-1 min-w-0">
                    <span className="font-semibold">{nearestExam.examName}</span>
                    {" "}— <span className="font-bold">{Math.max(0, daysUntil(nearestExam.examDate))} days</span> to go.{" "}
                    <span className="text-amber-700">Contacting a few teachers now keeps you on pace.</span>
                  </div>
                </div>
              )}

              {/* Main content + right rail */}
              <div className="flex gap-6 items-start">
                <div className="flex-1 min-w-0 space-y-6">
                  <NextStepsCard nextSteps={nextSteps} />
                  {ordered.filter(Boolean).map((node, i) => <div key={i}>{node}</div>)}
                  <ProfileNudges />
                </div>

                {/* Right rail */}
                <aside className="hidden lg:block w-72 shrink-0 sticky top-20 space-y-4">
                  <GroupClassesPanel subject={goal?.subject} gradeBand={goal?.gradeBand} curriculum={goal?.curriculum} onBrowse={openSearch} />
                  <CrashCoursesPanel subject={goal?.subject} gradeBand={goal?.gradeBand} curriculum={goal?.curriculum} onBrowse={openSearch} />
                  <ComingUpPanel
                    subject={goal?.subject}
                    gradeBand={goal?.gradeBand}
                    curriculum={goal?.curriculum}
                    onFindTeachers={openSearch}
                  />
                  {savedPanel}
                </aside>
              </div>

            </div>)}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
